// packages/web-ui/src/AppShell/drawer-tabs/ActivityTab.tsx
//
// W2-S9 — Activity tab body. Live agent log (UX §7.4) populated by the SSE
// dispatcher in `sse/attach-to-chat-slice.ts`, which appends every recorded
// event into the `eventLog` zustand slice.
//
// Migration final fix-pass — HIGH-3. Replaces the static empty-state
// placeholder. Reads `eventLog.entries` from the root store, renders a
// scrollable, keyboard-navigable list grouped by event type with per-row
// timestamp badge, source badge, and an expandable JSON payload preview.
// A Clear button calls `clearEventLog()`.
//
// Out-of-scope (still deferred to follow-ups):
//   - Pause / Export buttons (no streaming controls until a gating story
//     authors the bench-grade pause semantics).
//   - `GET /api/v1/diagnostics/agent-log/export` route.
//   - Filter chip strip (group-by-type already provides a summary; chip
//     strip is a UX polish that can land without slice changes).

import { useMemo, useState } from 'react';
import type { JSX, KeyboardEvent } from 'react';
import { useRootStore } from '../../state/store.js';
import {
  type EventLogEntry,
  type EventLogSource,
  selectEntriesGroupedByType,
} from '../../state/eventLog.js';

const SOURCE_BADGE_LABEL: Record<EventLogSource, string> = {
  chat: 'CHAT',
  kb: 'KB',
  validation: 'LINT',
  files: 'FILE',
  session: 'SESS',
  system: 'SYS',
  view: 'VIEW',
};

// Audi pass — uniform monochrome chrome on every source badge. Source
// is signalled by the label text alone; visual emphasis is reserved for
// the optional warning/error pulse via separate styling (see severity
// styling below). Avoids the multi-colour rainbow legacy palette.
const MONOCHROME_BADGE = 'border border-taupe-500 bg-cream-50 text-fg';
const SOURCE_BADGE_CLASS: Record<EventLogSource, string> = {
  chat: MONOCHROME_BADGE,
  kb: MONOCHROME_BADGE,
  validation: MONOCHROME_BADGE,
  files: MONOCHROME_BADGE,
  session: MONOCHROME_BADGE,
  system: MONOCHROME_BADGE,
  view: MONOCHROME_BADGE,
};

function formatTimestamp(timestamp: number): string {
  const d = new Date(timestamp);
  // HH:MM:SS.mmm — sufficient resolution for an activity log.
  const pad2 = (n: number): string => n.toString().padStart(2, '0');
  const pad3 = (n: number): string => n.toString().padStart(3, '0');
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}.${pad3(
    d.getMilliseconds(),
  )}`;
}

function formatPayloadPreview(payload: unknown): string {
  if (payload === null) return 'null';
  if (payload === undefined) return 'undefined';
  if (typeof payload === 'string') return payload;
  try {
    return JSON.stringify(payload, null, 2);
  } catch {
    return String(payload);
  }
}

export interface ActivityRowProps {
  entry: EventLogEntry;
  /** Controlled-expand state — when true, payload preview is shown. */
  expanded: boolean;
  /** Toggle handler — flips the expanded state for this entry. */
  onToggle: () => void;
}

export function ActivityRow({ entry, expanded, onToggle }: ActivityRowProps): JSX.Element {
  const handleKey = (e: KeyboardEvent<HTMLButtonElement>): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onToggle();
    }
  };
  return (
    <li
      data-testid="activity-row"
      data-event-type={entry.type}
      data-event-source={entry.source}
      data-expanded={expanded ? 'true' : 'false'}
      className="border-b border-taupe-200 last:border-b-0"
    >
      <button
        type="button"
        onClick={onToggle}
        onKeyDown={handleKey}
        aria-expanded={expanded}
        aria-controls={`activity-row-payload-${entry.id}`}
        className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-taupe-50 focus:outline-none focus:ring-2 focus:ring-taupe-900"
      >
        <span
          className="font-mono tabular-nums text-[0.65rem] text-taupe-600"
          data-testid="activity-row-timestamp"
        >
          {formatTimestamp(entry.timestamp)}
        </span>
        <span
          className={`shrink-0 rounded px-1.5 py-0.5 text-[0.6rem] font-semibold uppercase ring-1 ring-inset ${SOURCE_BADGE_CLASS[entry.source]}`}
          data-testid="activity-row-source-badge"
        >
          {SOURCE_BADGE_LABEL[entry.source]}
        </span>
        <span
          className="truncate font-medium text-taupe-900"
          data-testid="activity-row-type"
        >
          {entry.type}
        </span>
        <span aria-hidden="true" className="ml-auto text-taupe-500">
          {expanded ? '▾' : '▸'}
        </span>
      </button>
      {expanded ? (
        <pre
          id={`activity-row-payload-${entry.id}`}
          data-testid="activity-row-payload"
          className="overflow-x-auto bg-taupe-50 px-4 py-2 font-mono text-[0.65rem] text-taupe-800"
        >
          {formatPayloadPreview(entry.payload)}
        </pre>
      ) : null}
    </li>
  );
}

export function ActivityTab(): JSX.Element {
  const entries = useRootStore((s) => s.entries);
  const clearEventLog = useRootStore((s) => s.clearEventLog);
  const [expandedIds, setExpandedIds] = useState<ReadonlySet<string>>(() => new Set());

  const groups = useMemo(() => selectEntriesGroupedByType({ entries }), [entries]);

  const toggleExpanded = (id: string): void => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const isEmpty = entries.length === 0;

  return (
    <div
      id="drawer-panel-activity"
      role="tabpanel"
      aria-labelledby="drawer-tab-activity"
      data-testid="drawer-panel-activity"
      className="flex flex-1 flex-col overflow-hidden"
    >
      <div
        className="flex items-center justify-between border-b border-taupe-200 px-3 py-1.5 text-[0.65rem] font-semibold uppercase text-taupe-700"
        data-testid="activity-toolbar"
      >
        <span data-testid="activity-count">
          {entries.length} {entries.length === 1 ? 'event' : 'events'}
        </span>
        <button
          type="button"
          onClick={() => clearEventLog()}
          disabled={isEmpty}
          data-testid="activity-clear-button"
          className="rounded px-2 py-0.5 text-taupe-700 hover:bg-taupe-100 hover:text-taupe-900 focus:outline-none focus:ring-2 focus:ring-taupe-900 disabled:cursor-not-allowed disabled:text-taupe-400 disabled:hover:bg-transparent"
        >
          Clear
        </button>
      </div>
      {isEmpty ? (
        <div
          className="px-4 py-8 text-center text-xs italic text-taupe-700"
          data-testid="activity-empty-state"
        >
          No agent activity yet. Start a conversation.
        </div>
      ) : (
        <div
          className="flex-1 overflow-y-auto"
          data-testid="activity-scroll-region"
        >
          {groups.map(({ type, entries: groupEntries }) => (
            <section
              key={type}
              data-testid="activity-group"
              data-group-type={type}
              className="border-b border-taupe-200 last:border-b-0"
            >
              <header
                className="bg-taupe-50 px-3 py-1 text-[0.6rem] font-semibold uppercase tracking-wide text-taupe-700"
                data-testid="activity-group-header"
              >
                {type}
                <span className="ml-2 text-taupe-500">({groupEntries.length})</span>
              </header>
              <ul role="list" data-testid="activity-row-list">
                {groupEntries.map((entry) => (
                  <ActivityRow
                    key={entry.id}
                    entry={entry}
                    expanded={expandedIds.has(entry.id)}
                    onToggle={() => toggleExpanded(entry.id)}
                  />
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
